/* eslint-disable camelcase */
/* eslint-disable no-unused-expressions */
import React from 'react';
import { connect } from 'react-redux';
import { isLogin } from 'store/selectors/loginState';
import {
  fetchAreaCountries,
  fetchServiceProvider,
  fetchIntendedUsages,
  fetchCitations,
  fetchProductCategories,
  fetchCertificationTypes,
  fetchConnectedProviders,
  apiGetCountryList,
} from 'api';
import { actProductCategoryListFetch } from 'actions/productCategoryActions';
import T from 'i18n-react';
import { getLocale } from 'util/icwUtility';
import { get } from 'lodash';
import { useMe } from 'modules/company/hooks/useCompanySelector';
import LoadingScreen from 'icw-components/LoadingScreen';
import Constants from './constants';

export const StaticDataContext = React.createContext({
  country: [],
  productCategories: [],
});

const FetchByKey = {
  areaCountries: fetchAreaCountries,
  serviceProviders: async () => {
    return fetchServiceProvider();
  },
  intendedUsages: fetchIntendedUsages,
  citations: fetchCitations,
  productTestingServiceProvidersCommon: async () => {
    return fetchServiceProvider({ type: 'productTesting', category: 'common' });
  },
  productTestingServiceProvidersOptionals: async () => {
    return fetchServiceProvider({ type: 'productTesting', category: 'optional' });
  },
  icwServiceProviders: async () => {
    return fetchServiceProvider({ type: 'productTesting' });
  },
  productCategories: fetchProductCategories,
  certificationTypes: fetchCertificationTypes,
  connectedProviders: async (companyId) => {
    return fetchConnectedProviders(companyId);
  },
  exportMarket: apiGetCountryList,
};

const Provider = ({ children, isLogin }) => {
  const [isLoading, setLoading] = React.useState(false);
  const loaded = React.useRef(false);
  const loading = React.useRef(false);
  const refIsLogin = React.useRef(false);
  const { profile } = useMe();

  const data = React.useRef(
    new Map([
      ['serviceLevel', Constants.SERVICE_LEVEL],
      [
        'rating',
        [
          {
            label: T.translate('supplierManager.ratings.any'),
            value: '',
          },
          {
            label: T.translate('supplierManager.ratings.fourHalfAndUp'),
            value: '4.5',
          },
          {
            label: T.translate('supplierManager.ratings.fourAndUp'),
            value: '4',
          },
          {
            label: T.translate('supplierManager.ratings.threeHalfAndUp'),
            value: '3.5',
          },
          {
            label: T.translate('supplierManager.ratings.threeAndUp'),
            value: '3',
          },
          {
            label: T.translate('supplierManager.ratings.twoHalfAndUp'),
            value: '2.5',
          },
          {
            label: T.translate('supplierManager.ratings.twoAndUp'),
            value: '2',
          },
        ],
      ],
      ['productCategories', []],
      ['intendedUsages', []],
      ['areaCountries', []],
      ['serviceProviders', []],
      ['citations', []],
      ['exportMarket', []],
    ])
  );

  const getData = (key) => {
    return data.current.get(key) || [];
  };
  const getOptions = (key) => {
    const rawData = getData(key);
    return rawData.map((raw) => {
      const { id, name, fullname, country, companyName, ...rest } = raw;
      const optionLabel =
        get(rest, [`name_${getLocale().replace('-', '')}`]) || get(rest, 'name_en') || '';
      return {
        ...rest,
        value: id,
        label: fullname || name || optionLabel || country || companyName,
        id,
        country,
        name,
        raw,
      };
    });
  };

  const fetchStaticData = async () => {
    if (loading.current) {
      return;
    }
    loading.current = true;
    setLoading(true);
    await Object.keys(FetchByKey).reduce(async (acc, key) => {
      await acc;
      const fetch = FetchByKey[key];
      const params = [profile ? profile.companyId : ''];
      try {
        const response = await fetch(...params);
        const { status } = response;
        if (status === 200) {
          const { data: list } = response.data;
          data.current.set(key, list || response.data);
        }
      } catch (ex) {
        console.error(ex);
      }
    }, Promise.resolve());
    setLoading(false);
    loading.current = false;
    loaded.current = true;
  };

  const fetchData = async (tKey) => {
    const fetch = FetchByKey[tKey];
    const params = [profile ? profile.companyId : ''];
    const response = await fetch(...params);
    const { status } = response;
    if (status === 200) {
      const { data: list } = response.data;
      data.current.set(tKey, list || response.data);
    }
  };

  React.useEffect(() => {
    if (refIsLogin.current !== isLogin) {
      refIsLogin.current = isLogin;
      isLogin && !loaded.current && fetchStaticData();
    }
  }, [isLogin]);

  return (
    <StaticDataContext.Provider value={{ getOptions, getData, isLoading, fetchData }}>
      {isLoading ? <LoadingScreen className="loading-spinner" /> : children}
    </StaticDataContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  isLogin: isLogin(state),
  productCategories: state.productCategoryList,
});
const mapDispatchToProps = (dispatch) => ({
  fetchCategories: () => {
    dispatch(actProductCategoryListFetch());
  },
});
export const useStatic = () => {
  const context = React.useContext(StaticDataContext);
  return context;
};
export const StaticDataProvider = connect(mapStateToProps, mapDispatchToProps)(Provider);
